import {inject, ref, unref} from "vue";
import {CurrentUtils} from "xmgoing-server";
import {getAclOpertionBtns} from "@/main-page/hooks/useTableColumns";
import {GoingNewUtils, StorageUtils} from "xmgoing-utils";
import {useRoleAclHooks} from "@/main-page-base/ctls/containers/xmFormGroupPanel/hook/useRoleAclHooks";
import {
  isRowButtonDisabledByPageConfig,
  isRowButtonHiddenByPageConfig,
  rowButtonPageConfigLabel,
} from "@/main-page/ctls/hooks/rowButtonPageConfig";

/**
 * 获取是否为本租户记录
 */
function getIsAccountRecord({record}){
  let curUserTenantId = "";
  CurrentUtils._loadUser(false, (user) => {
    curUserTenantId = user['tenantId']
  })
  if (curUserTenantId === unref(record).tenantId) {
    return true;
  }else{
    return  false;
  }
}
/**
 * 获取按钮的显示标志
 * @param btnItem
 */
function getBtnFowFlag({btnItem,record,aclInfo,vuexObj}){
  if(GoingNewUtils.parseBoolean(btnItem.showFlagIsAccount)&&!getIsAccountRecord({record})){
    return  false;
  }
  if (!btnItem.showFunction) {
    try {
      btnItem.showFunction = new Function("record", "aclInfo", "vuex", `return (${btnItem.showFun})(record,aclInfo,vuex);`);
      return  btnItem.showFunction(unref(record), unref(aclInfo), vuexObj);
    } catch (e) {
      console.error(e, "======showFunction=====")
    }
  } else {
    return btnItem.showFunction(unref(record), unref(aclInfo), vuexObj);
  }
}

/**
 * 获取控制权限
 * 该函数用于根据当前状态、记录和按钮项来确定用户是否有某项操作的权限
 * @param {Object} param0 包含状态、记录、按钮项和可选函数名的对象
 * @param {any} state 应用程序的状态
 * @param {any} record 当前操作的记录
 * @param {Object} btnItem 按钮项，包含权限控制的相关函数
 * @param {string} funName 要调用的函数名，默认为'initFun'
 * @returns {boolean} 如果用户有权限执行操作，则返回true；否则返回false
 */
export function getCtlAcl({state, record, btnItem, funName = 'initFun'}) {
  // 获取当前用户的ID
  const aclInfo = {userId: StorageUtils.getSessionItem("curUserId")};
  // 创建一个包含当前状态的Vuex对象
  const vuexObj: any = {state: unref(state)}

  // 如果按钮项中有对应的函数名，则执行该函数
  if (btnItem[funName]) {
    // 如果按钮项中没有初始化函数，则创建一个新的函数
    if (!btnItem.initFunction) {
      // 创建一个新的函数，并将其赋值给btnItem.initFunction
      btnItem.initFunction = new Function("record", "aclInfo", "vuex", `return (${btnItem[funName]})(record,aclInfo,vuex);`);
      // 执行新创建的函数，并根据返回值决定是否返回true
      const res = btnItem.initFunction(unref(record), unref(aclInfo), vuexObj);
      if (res != null) {
        return !res;
      } else {
        return false;
      }
    } else {
      // 如果按钮项中有初始化函数，则直接执行该函数
      const res = btnItem.initFunction(unref(record), unref(aclInfo),vuexObj);
      if (res != null) {
        return !res;
      } else {
        return false;
      }
    }
  }
  // 如果按钮项中没有对应的函数名，则返回false
  return false;
}
/**
 * 获取按钮显示标志
 * @param btnItem
 */
const showBtnCtl = function ({record, state, btnItem, index}) {
  if(GoingNewUtils.parseBoolean(btnItem.showFlagIsAccount)&&!getIsAccountRecord({record})){
    return  false;
  }
  //如果存在按钮变量
  if (btnItem && btnItem.showCtlVuex) {
    try {
      const vuexObj: any = {state: unref(state), record: unref(record)}
      const fun = new Function("vuex", `return ${btnItem.showCtlVuex}`);
      return fun(vuexObj);
    } catch (e) {
      console.warn(e, "==initVuexPropVal==")
    }
  }
  return true;
}
/**
 * 获取按钮权限信息
 * @param ctlAclInfos
 * @param record
 * @param disabled
 * @param state
 * @param column
 */
export function useGridOperationBtnState({gridKey, ctlAclInfos, record, disabled, state, column, roleAppConfigs}: any) {
  // 与页面主按钮同一份生效配置。页面未注入时为空，行按钮保持设计器原样。
  const pageRoleConfigs = inject('pageRoleConfigs', ref([] as any[]));

  const hiddenByPageConfig = function (btnItem) {
    return isRowButtonHiddenByPageConfig(unref(pageRoleConfigs), btnItem);
  }

  /**
   * 显示按钮权限信息
   */
  const showBtnAclInfo=function (btnItem,rowIndex){
    if (hiddenByPageConfig(btnItem)) {
      return false;
    }
    if (btnItem?.isUserAclFlag === true || btnItem?.isUserAclFlag === 'true') {
      // 仅本人数据可见
      const createUserId = unref(record)?.create_user || unref(record)?.createUser;
      if (createUserId) {
        const curUserId = StorageUtils.getSessionItem("curUserId");
        if (curUserId !== createUserId) {
          return false;
        }
      }
    }

    const {roleAppAclFlag, hasCtlAlcFlag} = useRoleAclHooks({roleAppConfigs, props: btnItem});
    if (unref(hasCtlAlcFlag)) {
      // 价格权限控制
      if (!unref(roleAppAclFlag)) {
        return false;
      }
    }

    const btnList = getAclOpertionBtns(ctlAclInfos, {key: unref(gridKey)});
    const btnAcl=btnList[unref(btnItem).value];
    if(btnAcl==='hidden'){
      return  false;
    }
    return true;
  }

  /**
   * 获取显示的按钮集合
   */
  const getShowColumnList = computed(() => {
    const newList: any = [];
    if (unref(column) && unref(column).list && Array.isArray(unref(column).list)) {
      unref(column).list.forEach((btnItem: any, index: number) => {
        const flag = showBtnAclInfo(btnItem, index);
        if (flag) {
          const aclInfo = {
            userId: StorageUtils.getSessionItem("curUserId"),
            tenantId: CurrentUtils.currentUser['tenantId']
          };
          const vuexObj: any = {state: unref(state)}
          if (btnItem.showFun) {
            const res=getBtnFowFlag({btnItem,record,aclInfo,vuexObj});
            if (res != null && res !== false) {
              newList.push(btnItem);
            }
          } else {
            newList.push(btnItem);
          }
        }
      })
    }
    // 操作栏联查：运行时追加，不写入 list；仅看列配置 linkTraceFlag
    if (unref(column)?.linkTraceFlag === true) {
      newList.push({
        label: '联查',
        value: '__linkTrace',
        btnType: 'link',
        size: 'small',
        btnIcon: 'remix-icon ri-node-tree',
        isLinkTraceBtn: true,
      });
    }
    // 操作栏变更记录：运行时追加，不写入 list；仅看列配置 changeRecordFlag
    if (unref(column)?.changeRecordFlag === true) {
      newList.push({
        label: '变更记录',
        value: '__changeRecord',
        btnType: 'link',
        size: 'small',
        btnIcon: 'remix-icon ri-time-line',
        isChangeRecordBtn: true,
      });
    }
    return newList.filter((btnItem) => !hiddenByPageConfig(btnItem));
  })
  /**
   * 按钮初始化函数
   * @param btnItem
   */
  const getDisAbledFlag = function (btnItem) {
    if (isRowButtonDisabledByPageConfig(unref(pageRoleConfigs), btnItem)) {
      return true;
    }
    const btnList = getAclOpertionBtns(ctlAclInfos, {key: unref(gridKey)});
    const btnAcl=btnList[unref(btnItem).value];
    if(btnAcl==='disabled'){
      return  true;
    }
    if (unref(disabled) === true || (unref(disabled) + '') === 'true') {
      return unref(disabled)
    }
    return getCtlAcl({state, record, btnItem, funName: 'initFun'});
  }

  const getRowButtonLabel = function (btnItem) {
    return rowButtonPageConfigLabel(unref(pageRoleConfigs), btnItem);
  }

  return {
    showBtnAclInfo,
    getShowColumnList,
    showBtnCtl,
    getDisAbledFlag,
    getRowButtonLabel
  }
}
